/**
 * Service Purchase Component
 * Módulo para gerenciamento de compras de serviços
 */
const ServicePurchase = (function() {
    'use strict';

    // Configuração
    const config = {
        selectors: {
            component: '.service-purchase-component',
            purchaseButton: '.purchase-service-btn',
            cardTitle: '.card-title',
            originalPrice: '.original-price'
        },
        modalId: 'purchaseModal',
        paymentMethods: getGateways()
    };

    // Cache para elementos DOM frequentemente usados
    const elements = {};

    /**
     * Inicializa o componente
     */
    function init() {
        bindEvents();
    }

    /**
     * Associa eventos aos elementos DOM
     */
    function bindEvents() {
        const purchaseButtons = document.querySelectorAll(
            `${config.selectors.component} ${config.selectors.purchaseButton}`
        );
        
        purchaseButtons.forEach(button => {
            button.addEventListener('click', handlePurchaseClick);
        });
    }

    /**
     * Manipula o clique no botão de compra
     * @param {Event} event - Evento de clique
     */
    function handlePurchaseClick(event) {
        const button = event.currentTarget;
        const component = button.closest(config.selectors.component);
        
        if (!component) return;
        
        const serviceData = {
            id: component.dataset.serviceId,
            name: component.querySelector(config.selectors.cardTitle).textContent.trim(),
            price: component.querySelector(config.selectors.originalPrice).textContent.trim()
        };
        
        createAndShowModal(serviceData);
    }

    /**
     * Cria e exibe o modal de compra
     * @param {Object} serviceData - Dados do serviço
     */
    function createAndShowModal(serviceData) {
        const modalContent = createModalContent(serviceData);
        const modal = createModal(modalContent);
        
        // Armazena a instância do modal para uso posterior
        window.currentPurchaseModal = modal;
        
        // Configura ouvintes de eventos para o modal
        setupModalEventListeners(serviceData.id);
        
        // Configura máscaras de entrada para CPF e telefone
        setupInputMasks();
    }

    /**
     * Cria o conteúdo HTML do modal
     * @param {Object} serviceData - Dados do serviço
     * @returns {string} HTML do conteúdo do modal
     */
    function createModalContent(serviceData) {
        return `
            <div class="mwModal-content service-purchase-modal">
                <div class="mwModal-header">
                    <h3 class="mwModal-title">${translate('Purchase')} ${serviceData.name}</h3>
                    <button type="button" class="mwModal-close" data-dismiss="modal">
                        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                        </svg>
                        <span class="sr-only">Close modal</span>
                    </button>
                </div>
                <div class="mwModal-body">
                    <div class="mwModal-service-info">
                        <h6 class="mwModal-service-name">${serviceData.name}</h6>
                        <p class="mwModal-service-price">${serviceData.price}</p>
                    </div>
                    
                    ${createCouponField()}
                    ${createPaymentMethodSelector()}
                    ${createPixFields()}
                </div>
                <div class="mwModal-footer">
                    <button type="button" data-dismiss="modal" class="mwModal-button secondary mr-2">
                        ${translate('Cancel')}
                    </button>
                    <button id="confirm-purchase-btn" data-service-id="${serviceData.id}" 
                            type="button" class="mwModal-button primary">
                        ${translate('Confirm Purchase')}
                    </button>
                </div>
            </div>
        `;
    }

    /**
     * Cria o campo de cupom
     * @returns {string} HTML do campo de cupom
     */
    function createCouponField() {
        return `
            <div class="mwModal-form-group">
                <label for="coupon-input" class="mwModal-label">${translate('Coupon Code')}</label>
                <div class="mwModal-coupon-input">
                    <input type="text" id="coupon-input" class="mwModal-input" 
                           placeholder="${translate('Enter coupon code')}">
                    <button id="apply-coupon-btn" type="button">${translate('Apply')}</button>
                </div>
                <div class="coupon-message mwModal-message"></div>
            </div>
        `;
    }

    /**
     * Cria o seletor de método de pagamento
     * @returns {string} HTML do seletor de método de pagamento
     */
    function createPaymentMethodSelector() {

        if (availableGateways && availableGateways.length > 0) {
            let paymentMethodsHtml = '';
            
            // Iterate through available gateways
            availableGateways.forEach(gateway => {
                if (gateway && gateway.code) {
                    paymentMethodsHtml += `
                    <div class="payment-method-item" data-method="${gateway.code}">
                        <img src="/public/assets/mwCore/images/payments/${gateway.code}.svg" alt="${gateway.name}" />
                        <span class="payment-method-name">${gateway.name}</span>
                    </div>`;
                }
            });
            
            return `
            <div class="mwModal-form-group">
                <label class="mwModal-label">
                    ${translate('Payment Method')}
                </label>
                <div id="payment-method-options">
                    ${paymentMethodsHtml}
                </div>
                <input type="hidden" id="payment-method-select" value="" />
            </div>
            `;
        }
        
        // Fallback to a message if no gateways available
        return `
        <div class="mwModal-form-group">
            <label class="mwModal-label">
                ${translate('Payment Method')}
            </label>
            <div id="payment-method-options" class="text-center p-4">
                <p class="text-red-500">${translate('No payment methods available at the moment.')}</p>
            </div>
            <input type="hidden" id="payment-method-select" value="" />
        </div>
        `;
    }

    /**
     * Cria os campos específicos para PIX
     * @returns {string} HTML dos campos de PIX
     */
    function createPixFields() {
        return `
            <div id="pix-fields" class="hidden space-y-3">
                <div class="mwModal-form-group">
                    <label for="pix-name" class="mwModal-label">${translate('Full Name')}</label>
                    <input type="text" id="pix-name" class="mwModal-input">
                </div>
                <div class="mwModal-form-group">
                    <label for="pix-cpf" class="mwModal-label">CPF</label>
                    <input type="text" id="pix-cpf" class="mwModal-input" placeholder="000.000.000-00">
                </div>
                <div class="mwModal-form-group">
                    <label for="pix-phone" class="mwModal-label">${translate('Phone Number')}</label>
                    <input type="text" id="pix-phone" class="mwModal-input" placeholder="+55 (00) 00000-0000">
                </div>
            </div>
        `;
    }

    /**
     * Cria o modal usando a biblioteca disponível ou implementação personalizada
     * @param {string} modalContent - Conteúdo HTML do modal
     * @returns {Object} Instância do modal
     */
    function createModal(modalContent) {
        // Verifica qual implementação de modal está disponível
        if (isFlowbiteAvailable() && typeof flowbite.Modal !== 'undefined') {
            return createFlowbiteModal(modalContent);
        } else if (isCustomModalAvailable()) {
            return createCustomModal(modalContent);
        } else {
            return createBasicModal(modalContent);
        }
    }

    /**
     * Verifica se a biblioteca Flowbite está disponível
     * @returns {boolean} Disponibilidade da biblioteca
     */
    function isFlowbiteAvailable() {
        return typeof window.flowbite !== 'undefined';
    }

    /**
     * Verifica se a implementação customizada de modal está disponível
     * @returns {boolean} Disponibilidade da implementação
     */
    function isCustomModalAvailable() {
        return typeof window.servicePurchaseModal !== 'undefined';
    }

    /**
     * Cria um modal usando a biblioteca Flowbite
     * @param {string} modalContent - Conteúdo HTML do modal
     * @returns {Object} Instância do modal Flowbite
     */
    function createFlowbiteModal(modalContent) {
        // Remove o modal existente se presente
        let existingModalEl = document.getElementById(config.modalId);
        if (existingModalEl) {
            existingModalEl.remove();
        }
        
        // Cria a estrutura do modal Flowbite
        const flowbiteModalEl = document.createElement('div');
        flowbiteModalEl.id = config.modalId;
        flowbiteModalEl.setAttribute('tabindex', '-1');
        flowbiteModalEl.setAttribute('aria-hidden', 'true');
        flowbiteModalEl.className = 'mwModal hidden';
        flowbiteModalEl.innerHTML = `<div class="mwModal-container">${modalContent}</div>`;
        
        document.body.appendChild(flowbiteModalEl);
        
        // Inicializa e exibe o modal Flowbite
        const modal = new flowbite.Modal(document.getElementById(config.modalId), {
            closable: true
        });
        
        modal.show();
        return modal;
    }

    /**
     * Cria um modal usando a implementação customizada
     * @param {string} modalContent - Conteúdo HTML do modal
     * @returns {Object} Instância do modal customizado
     */
    function createCustomModal(modalContent) {
        return window.servicePurchaseModal.show({
            id: config.modalId,
            content: modalContent,
            containerClass: 'mwModal-container'
        });
    }

    /**
     * Gets the available payment gateways from the window object
     * @returns {Array} Array of payment gateways
     */
    function getGateways() {
        // Return the gateways array or empty array if not available
        return window.gateways || [];
    }

    /**
     * Cria um modal básico sem dependência de bibliotecas
     * @param {string} modalContent - Conteúdo HTML do modal
     * @returns {Object} API do modal
     */
    function createBasicModal(modalContent) {
        // Cria um modal básico
        const modalOverlay = document.createElement('div');
        modalOverlay.className = 'mwModal';
        modalOverlay.id = config.modalId;
        
        const modalContainer = document.createElement('div');
        modalContainer.className = 'mwModal-container';
        modalContainer.innerHTML = modalContent;
        
        modalOverlay.appendChild(modalContainer);
        document.body.appendChild(modalOverlay);
        
        // Adiciona manipulador de clique para botões de fechar
        modalOverlay.querySelectorAll('[data-dismiss="modal"]').forEach(button => {
            button.addEventListener('click', () => {
                document.body.removeChild(modalOverlay);
            });
        });
        
        // Fecha ao clicar no fundo
        modalOverlay.addEventListener('click', e => {
            if (e.target === modalOverlay) {
                document.body.removeChild(modalOverlay);
            }
        });
        
        // Cria uma API similar ao modal do Bootstrap
        return {
            element: modalOverlay,
            hide: function() {
                if (document.body.contains(modalOverlay)) {
                    document.body.removeChild(modalOverlay);
                }
            }
        };
    }

    /**
     * Configura ouvintes de eventos para o modal
     * @param {string} serviceId - ID do serviço
     */
    function setupModalEventListeners(serviceId) {
        setupPaymentMethodChange();
        setupCouponButton(serviceId);
        setupConfirmButton(serviceId);
    }

    /**
     * Configura o ouvinte de mudança do método de pagamento
     */
    function setupPaymentMethodChange() {
        const paymentItems = document.querySelectorAll('.payment-method-item');
        const paymentMethodSelect = document.getElementById('payment-method-select');
        
        if (!paymentItems.length || !paymentMethodSelect) return;
        
        paymentItems.forEach(item => {
            item.addEventListener('click', function() {
                // Remove selection from all items
                paymentItems.forEach(i => i.classList.remove('selected'));
                
                // Add selection to clicked item
                this.classList.add('selected');
                
                // Update hidden input
                const method = this.dataset.method;
                paymentMethodSelect.value = method;
                
                // Toggle PIX fields visibility if the method is paghiper (PIX)
                const pixFields = document.getElementById('pix-fields');
                if (!pixFields) return;
                
                // Check if the selected payment method is paghiper (Brazilian PIX)
                const isPix = method === 'paghiper';
                pixFields.classList.toggle('hidden', !isPix);
                
                // Set required attribute on PIX fields based on selection
                const pixInputs = pixFields.querySelectorAll('input');
                pixInputs.forEach(input => {
                    input.required = isPix;
                    // Clear validation state when switching payment methods
                    input.classList.remove('mwModal-field-error', 'mwModal-field-success');
                });
            });
        });
    }

    /**
     * Configura o botão de aplicar cupom
     * @param {string} serviceId - ID do serviço
     */
    function setupCouponButton(serviceId) {
        const applyCouponBtn = document.getElementById('apply-coupon-btn');
        if (!applyCouponBtn) return;
        
        applyCouponBtn.addEventListener('click', function() {
            const couponInput = document.getElementById('coupon-input');
            const couponMessage = document.querySelector('.coupon-message');
            
            if (!couponInput || !couponMessage) return;
            
            const couponCode = couponInput.value.trim();
            if (!couponCode) {
                showCouponMessage(couponMessage, translate('Please enter a coupon code'), 'error');
                return;
            }
            
            // Mostra mensagem de carregamento
            showCouponMessage(couponMessage, translate('Verifying coupon...'), 'loading');
            
            // Chama API para validar cupom
            applyCoupon(serviceId, couponCode, couponMessage);
        });
    }

    /**
     * Exibe mensagem relacionada ao cupom
     * @param {HTMLElement} element - Elemento para mostrar a mensagem
     * @param {string} message - Mensagem a ser exibida
     * @param {string} type - Tipo de mensagem (error, success, loading)
     */
    function showCouponMessage(element, message, type) {
        element.textContent = message;
        element.className = `coupon-message mwModal-message ${type}`;
    }

    /**
     * Configura o botão de confirmar compra
     * @param {string} serviceId - ID do serviço
     */
    function setupConfirmButton(serviceId) {
        const confirmPurchaseBtn = document.getElementById('confirm-purchase-btn');
        if (!confirmPurchaseBtn) return;
        
        confirmPurchaseBtn.addEventListener('click', function() {
            const paymentMethodSelect = document.getElementById('payment-method-select');
            if (!paymentMethodSelect) return;
            
            const paymentMethod = paymentMethodSelect.value;
            
            // Valida o formulário diretamente
            if (!validateForm()) {
                return; // Para se a validação falhar
            }
            
            // Prepara dados da compra
            const purchaseData = {
                service_id: serviceId,
                gateway: paymentMethod,
                coupon_code: document.getElementById('coupon-input')?.value.trim() || null
            };
            
            // Adiciona dados específicos do PIX se necessário
            if (paymentMethod === 'paghiper') {
                purchaseData.name = document.getElementById('pix-name')?.value.trim() || '';
                purchaseData.cpf = document.getElementById('pix-cpf')?.value.trim() || '';
                purchaseData.phone = document.getElementById('pix-phone')?.value.trim() || '';
            }
            
            // Processa a compra
            processPurchase(purchaseData);
        });
    }

    /**
     * Configura máscaras de entrada para CPF e telefone
     */
    function setupInputMasks() {
        setupCPFMask();
        setupPhoneMask();
    }

    /**
     * Configura máscara para o campo de CPF
     */
    function setupCPFMask() {
        const cpfInput = document.getElementById('pix-cpf');
        if (!cpfInput) return;
        
        cpfInput.addEventListener('input', function(e) {
            let value = e.target.value.replace(/\D/g, ''); // Remove não-dígitos
            
            if (value.length > 11) {
                value = value.slice(0, 11);
            }
            
            // Aplica máscara CPF (000.000.000-00)
            if (value.length > 0) {
                value = value.replace(/(\d{3})(\d)/, '$1.$2');
            }
            if (value.length > 4) {
                value = value.replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3');
            }
            if (value.length > 8) {
                value = value.replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
            }
            
            e.target.value = value;
            
            // Validação em tempo real
            if (value.length === 14) { // 11 dígitos + 3 caracteres de formatação
                const cpfDigits = value.replace(/\D/g, '');
                if (validateCPF(cpfDigits)) {
                    showFieldSuccess(cpfInput);
                } else {
                    showFieldError(cpfInput, translate('Invalid CPF'));
                }
            }
        });
    }

    /**
     * Configura máscara para o campo de telefone
     */
    function setupPhoneMask() {
        const phoneInput = document.getElementById('pix-phone');
        if (!phoneInput) return;
        
        phoneInput.addEventListener('input', function(e) {
            let value = e.target.value.replace(/\D/g, ''); // Remove não-dígitos
            
            if (value.length > 13) {
                value = value.slice(0, 13);
            }
            
            // Aplica máscara de telefone (+55 (00) 00000-0000)
            if (value.length > 0) {
                value = '+' + value;
            }
            if (value.length > 3) {
                value = value.slice(0, 3) + ' (' + value.slice(3);
            }
            if (value.length > 7) {
                value = value.slice(0, 7) + ') ' + value.slice(7);
            }
            if (value.length > 13) {
                value = value.slice(0, 13) + '-' + value.slice(13);
            }
            
            e.target.value = value;
        });
    }

    /**
     * Aplica um cupom ao serviço
     * @param {string} serviceId - ID do serviço
     * @param {string} couponCode - Código do cupom
     * @param {HTMLElement} messageElement - Elemento para exibir mensagens
     */
    function applyCoupon(serviceId, couponCode, messageElement) {
        // Verifica se o app está disponível
        if (!isAppAvailable()) {
            showCouponMessage(messageElement, translate('Error: App not initialized'), 'error');
            return;
        }
        
        // Chama a API para validar o cupom
        window.app.ajaxy('/payment/applyCoupon', 'POST', {
            coupon_code: couponCode,
            product_id: serviceId
        }, false, function(response) {
            if (response.success) {
                showCouponMessage(messageElement, translate('Coupon applied successfully'), 'success');
                
                // Atualiza exibição de preço se necessário
                updatePriceDisplay(response);
            } else {
                showCouponMessage(
                    messageElement, 
                    response.message || translate('Invalid coupon code'), 
                    'error'
                );
            }
        });
    }

    /**
     * Atualiza a exibição do preço após aplicar um cupom
     * @param {Object} response - Resposta da API de cupom
     */
    function updatePriceDisplay(response) {
        const priceElement = document.querySelector('.service-info .price');
        if (!priceElement) return;
        
        const originalPrice = priceElement.textContent;
        priceElement.innerHTML = `
            <span class="line-through text-gray-400">${originalPrice}</span>
            <span class="text-green-500 font-medium">
                ${response.discount} ${response.currency}
            </span>
        `;
    }

    /**
     * Valida o formulário de compra
     * @returns {boolean} Resultado da validação
     */
    function validateForm() {
        const paymentMethodSelect = document.getElementById('payment-method-select');
        let isValid = true;
        
        if (!paymentMethodSelect || !paymentMethodSelect.value) {
            // Show error message for payment method selection
            const paymentMethodOptions = document.getElementById('payment-method-options');
            if (paymentMethodOptions) {
                const errorMessage = document.createElement('div');
                errorMessage.className = 'mwModal-message error';
                errorMessage.textContent = translate('Please select a payment method');
                
                // Remove existing error message if any
                const existingError = paymentMethodOptions.nextElementSibling;
                if (existingError && existingError.classList.contains('mwModal-message')) {
                    existingError.remove();
                }
                
                paymentMethodOptions.parentNode.insertBefore(errorMessage, paymentMethodOptions.nextSibling);
            }
            isValid = false;
        }
        
        // Valida campos PIX se PIX estiver selecionado
        if (paymentMethodSelect && paymentMethodSelect.value === 'paghiper') {
            isValid = validatePixFields() && isValid;
        }
        
        return isValid;
    }

    /**
     * Valida os campos específicos do PIX
     * @returns {boolean} Resultado da validação
     */
    function validatePixFields() {
        let isValid = true;
        
        // Obtém os campos
        const nameField = document.getElementById('pix-name');
        const cpfField = document.getElementById('pix-cpf');
        const phoneField = document.getElementById('pix-phone');
        
        // Valida nome
        if (!nameField?.value.trim()) {
            showFieldError(nameField, translate('Full name is required'));
            isValid = false;
        } else {
            showFieldSuccess(nameField);
        }
        
        // Valida CPF
        const cpfValue = cpfField?.value.trim().replace(/\D/g, '') || '';
        if (!cpfValue) {
            showFieldError(cpfField, translate('CPF is required'));
            isValid = false;
        } else if (!validateCPF(cpfValue)) {
            showFieldError(cpfField, translate('Invalid CPF format'));
            isValid = false;
        } else {
            showFieldSuccess(cpfField);
        }
        
        // Valida telefone
        const phoneValue = phoneField?.value.trim().replace(/\D/g, '') || '';
        if (!phoneValue) {
            showFieldError(phoneField, translate('Phone number is required'));
            isValid = false;
        } else if (phoneValue.length < 10) {
            showFieldError(phoneField, translate('Phone number is too short'));
            isValid = false;
        } else {
            showFieldSuccess(phoneField);
        }
        
        return isValid;
    }

    /**
     * Exibe erro em um campo do formulário
     * @param {string|HTMLElement} field - Campo ou ID do campo
     * @param {string} message - Mensagem de erro
     */
    function showFieldError(field, message) {
        const element = typeof field === 'string' ? document.getElementById(field) : field;
        if (!element) return;
        
        element.classList.add('mwModal-field-error');
        element.classList.remove('mwModal-field-success');
        
        // Adiciona mensagem de erro se não existir
        let errorMessage = element.nextElementSibling;
        if (!errorMessage || !errorMessage.classList.contains('mwModal-message')) {
            errorMessage = document.createElement('div');
            errorMessage.className = 'mwModal-message error';
            element.parentNode.insertBefore(errorMessage, element.nextSibling);
        }
        
        errorMessage.textContent = message;
    }

    /**
     * Exibe sucesso em um campo do formulário
     * @param {HTMLElement} field - Campo
     */
    function showFieldSuccess(field) {
        if (!field) return;
        
        field.classList.remove('mwModal-field-error');
        field.classList.add('mwModal-field-success');
        
        // Remove mensagem de erro se existir
        const errorMessage = field.nextElementSibling;
        if (errorMessage && errorMessage.classList.contains('mwModal-message')) {
            errorMessage.remove();
        }
    }

    /**
     * Verifica se o CPF é válido
     * @param {string} cpf - CPF a ser validado
     * @returns {boolean} Resultado da validação
     */
    function validateCPF(cpf) {
        // CPF deve ter 11 dígitos
        if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
            return false;
        }
        
        // Calcula o primeiro dígito verificador
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf.charAt(i)) * (10 - i);
        }
        
        let remainder = sum % 11;
        let digit1 = remainder < 2 ? 0 : 11 - remainder;
        
        // Calcula o segundo dígito verificador
        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf.charAt(i)) * (11 - i);
        }
        
        remainder = sum % 11;
        let digit2 = remainder < 2 ? 0 : 11 - remainder;
        
        // Verifica se os dígitos calculados coincidem com os do CPF
        return digit1 === parseInt(cpf.charAt(9)) && digit2 === parseInt(cpf.charAt(10));
    }

    /**
     * Processa a compra
     * @param {Object} purchaseData - Dados da compra
     */
    function processPurchase(purchaseData) {
        // Verifica se o app está disponível
        if (!isAppAvailable()) {
            showAlert({
                icon: 'error',
                title: translate('Error'),
                text: translate('Error: App not initialized')
            });
            return;
        }
        
        // Valida o formulário antes do envio
        if (!validateForm()) {
            return; // Para se a validação falhar
        }
        
        // Fecha o modal
        closeCurrentModal();
        
        // Mostra loading
        showLoadingAlert();
        
        // Chama a API para processar a compra
        callCheckoutAPI(purchaseData);
    }

    /**
     * Verifica se o app está disponível
     * @returns {boolean} Disponibilidade do app
     */
    function isAppAvailable() {
        return window.app && typeof window.app.ajaxy === 'function';
    }

    /**
     * Exibe alerta de carregamento
     */
    function showLoadingAlert() {
        showAlert({
            title: translate('Processing'),
            text: translate('Please wait while we process your payment...'),
            allowOutsideClick: false,
            didOpen: typeof Swal !== 'undefined' ? () => {
                Swal.showLoading();
            } : null
        });
    }

    /**
     * Chama a API de checkout
     * @param {Object} purchaseData - Dados da compra
     */
    function callCheckoutAPI(purchaseData) {
        fetch('/payment/checkout-service', {
            method: 'POST',
            body: JSON.stringify(purchaseData),
            headers: {
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => handleCheckoutResponse(data))
        .catch(error => {
            console.error('Fetch error:', error);
            showAlert({
                icon: 'error',
                title: translate('Error'),
                text: translate('Connection error occurred. Please try again.')
            });
        });
    }

    /**
     * Trata a resposta da API de checkout
     * @param {Object} data - Dados da resposta
     */
    function handleCheckoutResponse(data) {
        // Registra a resposta para depuração
        console.log('Checkout API response:', data);
        
        // Redirecionamento direto se houver URL
        if (data.redirect) {
            console.log('Redirecting to:', data.redirect);
            window.location.href = data.redirect;
            return;
        }
        
        // Processamento de Stripe
        if (data.session_id && window.Stripe) {
            handleStripeCheckout(data);
            return;
        }
        
        // Processamento de QR code PIX
        if (data.qrcode) {
            showQRCodeAlert(data.qrcode);
            return;
        }
        
        // Mensagem genérica de sucesso
        if (data.status === 'success') {
            showSuccessAlert();
            return;
        }
        
        // Tratamento de erro se chegou até aqui
        showAlert({
            icon: 'error',
            title: translate('Error'),
            text: data.message || translate('An error occurred processing your payment')
        });
    }

    /**
     * Manipula o checkout do Stripe
     * @param {Object} data - Dados de sessão do Stripe
     */
    function handleStripeCheckout(data) {
        const stripe = Stripe(data.stripe_pk);
        stripe.redirectToCheckout({
            sessionId: data.session_id
        }).then(function(result) {
            if (result.error) {
                showAlert({
                    icon: 'error',
                    title: translate('Error'),
                    text: result.error.message
                });
            }
        });
    }

    /**
     * Exibe alerta com QR code
     * @param {string} qrcode - QR code em base64
     */
    function showQRCodeAlert(qrcode) {
        showAlert({
            title: translate('Payment QR Code'),
            html: `
                <div class="text-center">
                    <p class="mb-4">${translate('Scan the QR code to complete your payment')}</p>
                    <img src="data:image/png;base64,${qrcode}" class="mx-auto" />
                </div>
            `,
            showConfirmButton: true,
            confirmButtonText: translate('I\'ve completed payment')
        });
    }

    /**
     * Exibe alerta de sucesso
     */
    function showSuccessAlert() {
        showAlert({
            icon: 'success',
            title: translate('Success'),
            text: translate('Your purchase is being processed'),
            timer: 3000
        });
    }

    /**
     * Fecha o modal atual
     */
    function closeCurrentModal() {
        if (!window.currentPurchaseModal) return;
        
        try {
            // Flowbite modal
            if (isFlowbiteAvailable() && 
                window.currentPurchaseModal.hide && 
                typeof window.currentPurchaseModal.hide === 'function') {
                window.currentPurchaseModal.hide();
            } 
            // Implementação customizada
            else if (typeof window.currentPurchaseModal.hide === 'function') {
                window.currentPurchaseModal.hide();
            }
            // Fallback
            else if (window.currentPurchaseModal.element && 
                     document.body.contains(window.currentPurchaseModal.element)) {
                document.body.removeChild(window.currentPurchaseModal.element);
            }
        } catch (error) {
            console.error('Error closing modal:', error);
        }
    }

    /**
     * Função auxiliar para exibir alertas, usando SweetAlert se disponível ou alerta do navegador como fallback
     * @param {Object} options - Opções do alerta
     */
    function showAlert(options) {
        if (typeof Swal !== 'undefined') {
            Swal.fire(options);
        } else {
            alert(options.text || options.title);
        }
    }

    /**
     * Função auxiliar para traduções
     * @param {string} text - Texto a ser traduzido
     * @returns {string} Texto traduzido
     */
    function translate(text) {
        return window.app && window.app.__ ? window.app.__(text) : text;
    }

    // API pública
    return {
        init: init
    };
})();

// Inicializa o módulo se o app estiver pronto, caso contrário aguarda DOMContentLoaded
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    if (window.app) {
        ServicePurchase.init();
    } else {
        document.addEventListener('DOMContentLoaded', function() {
            if (window.app) {
                ServicePurchase.init();
            }
        });
    }
}

export default ServicePurchase;